import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as d3 from "d3";

/**
 * Multi-series sparkline drawn inside a grid cell.
 * A single click anywhere on the sparkline opens a popup (like the KPI chart's
 * fullscreen view) with the full series drawn as a line chart with axes.
 *
 * Column config (selected_column_parent entry):
 *   lineChartVisiblity:     true
 *   lineChartX_Axis:        "period"
 *   lineChartY_Axis:        "fill_rate_by_month,abandoned_rate_month"
 *   lineChart_Tooltip_Name: "Fill Rate,Abandoned Rate"   (tooltip labels, same order as lineChartY_Axis)
 *   lineChart_DefaultView:  "12"                         (optional, show only the last N points in the cell)
 *   lineChartColors:        "#2ecc71,#d6164a"            (optional, one per Y field)
 *   lineChart_XAxisLabel:   "Month"                      (optional, popup x-axis title)
 *   lineChart_YAxisLabel:   "Rate"                       (optional, popup y-axis title)
 *   lineChart_Format:       "number" | "percent"         (optional, "percent" appends % to every value)
 *   lineChart_CategoryField: "payer_name"                (optional, popup category card; default: row group / first column)
 *   lineChart_CountField:   "patient_count"              (optional, popup count card; default: first column whose header has "patient")
 *   lineChart_InverseTrend: "abandoned_rate_month"       (optional, series where a rise is bad → red ▲ / green ▼;
 *                                                        default: fields or labels containing "abandon")
 *
 * Popup cards: category, patient count, and a "% Change <series>" card per Y field comparing
 * the latest data point with the previous one: ((latest - previous) / previous) × 100.
 *
 * Cell value: JSON string (or array) of points, e.g.
 *   '[{"period":"2021-10-01T00:00:00","fill_rate_by_month":0,"abandoned_rate_month":100}, ...]'
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-basic-sparkline-grid',
  standalone: false,
  template: `
    <div #gridSparklineContainer class="grid-sparkline"></div>

    <!-- Popup line chart (rendered via CDK Overlay) -->
    <ng-template #popupTemplate>
      <div class="sparkline-popup-container" (click)="closePopup()">
        <div class="sparkline-popup-content" (click)="$event.stopPropagation()">
          <div class="sparkline-popup-header">
            <h2 class="sparkline-popup-title" [attr.title]="popupTitle">{{ popupTitle }}</h2>
            <button type="button" class="sparkline-popup-close" title="Close" (click)="closePopup()">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.9501 0.409871L8 5.35998L3.04989 0.409871C2.5034 -0.136624 1.61634 -0.136624 1.06985 0.409871L0.409871 1.06985C-0.136624 1.61634 -0.136624 2.5034 0.409871 3.04989L5.35998 8L0.409871 12.9501C-0.136624 13.4966 -0.136624 14.3837 0.409871 14.9302L1.06985 15.5901C1.61634 16.1366 2.5034 16.1366 3.04989 15.5901L8 10.64L12.9501 15.5901C13.4966 16.1366 14.3837 16.1366 14.9302 15.5901L15.5901 14.9302C16.1366 14.3837 16.1366 13.4966 15.5901 12.9501L10.64 8L15.5901 3.04989C16.1366 2.5034 16.1366 1.61634 15.5901 1.06985L14.9302 0.409871C14.3837 -0.136624 13.4966 -0.136624 12.9501 0.409871Z" fill="#1363DF" />
              </svg>
            </button>
          </div>
          <!-- Summary cards: row category, patient count, then % change per series -->
          <div class="sparkline-popup-cards" *ngIf="popupCards.length">
            <div class="sparkline-popup-card" *ngFor="let card of popupCards">
              <div class="card-label" [attr.title]="card.label">{{ card.label }}</div>
              <div class="card-value" [attr.title]="card.value">
                <span class="card-arrow" *ngIf="card.arrow" [style.color]="card.color">{{ card.arrow }}</span>
                <span class="card-value-text">{{ card.value }}</span>
              </div>
            </div>
          </div>
          <div class="sparkline-popup-legend">
            <span class="legend-item" *ngFor="let field of yFields; let i = index">
              <span class="legend-dot" [style.background]="colors[i]"></span>{{ labels[i] || field }}
            </span>
          </div>
          <div class="sparkline-popup-chart"></div>
        </div>
      </div>
    </ng-template>
  `,
  styles: [`
    :host { display: block; width: 100%; }
    .grid-sparkline { width: 100%; line-height: 0; }

    ::ng-deep .grid-sparkline-popup-backdrop {
      background-color: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(4px);
    }
    .sparkline-popup-container {
      width: 100vw;
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      box-sizing: border-box;
    }
    .sparkline-popup-content {
      background: #ffffff;
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      width: 100%;
      max-width: 900px;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      line-height: normal;
    }
    .sparkline-popup-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid #e5e7eb;
      gap: 16px;
    }
    .sparkline-popup-title {
      margin: 0;
      font-size: 14px !important;
      font-weight: 600;
      color: #101d42;
      text-transform: capitalize;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-family: 'Poppins';
    }
    .sparkline-popup-close {
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 4px;
      display: flex;
      flex-shrink: 0;
    }
    .sparkline-popup-legend {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      padding: 14px 20px 0;
      font-size: 12px;
      color: #4b5563;
      font-family: 'Poppins';
    }
    .sparkline-popup-cards {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
      gap: 16px;
      padding: 18px 20px 4px;
    }
    .sparkline-popup-card {
      background: #f6f7f9;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 14px 16px;
      min-width: 0;
      font-family: 'Poppins';
    }
    .sparkline-popup-card .card-label {
      font-size: 12px;
      color: #6b7280;
      margin-bottom: 6px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .sparkline-popup-card .card-value {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      font-weight: 600;
      color: #101d42;
      min-width: 0;
    }
    /* Long category names wrap to a second line; anything longer ends in "…" (full text on hover) */
    .sparkline-popup-card .card-value-text {
      min-width: 0;
      line-height: 1.35;
      overflow-wrap: anywhere;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .sparkline-popup-card .card-arrow { font-size: 13px; line-height: 1; flex-shrink: 0; }
    @media (max-width: 768px) {
      .sparkline-popup-container { padding: 12px; }
      .sparkline-popup-content { max-width: 100%; max-height: 95vh; border-radius: 8px; }
      .sparkline-popup-header { padding: 12px 16px; }
      .sparkline-popup-title { font-size: 16px !important; }
      .sparkline-popup-chart { min-height: 200px; }
    }
    .legend-item { display: flex; align-items: center; gap: 8px; }
    .legend-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
    .sparkline-popup-chart {
      /* Content-sized like the KPI popup: the chart (incl. brush strip) asks for 360px
         and shrinks down to 250px when the 90vh cap is reached */
      flex: 1 1 360px;
      height: 360px;
      min-height: 250px;
      margin: 12px 20px 20px;
      background-color: #f9fafb;
      border-radius: 8px;
      overflow: hidden;
    }
  `]
})
export class BasicSparklineGridComponent implements ICellRendererAngularComp, AfterViewInit, OnDestroy {

  @ViewChild("gridSparklineContainer", { static: true }) gridSparklineContainer!: ElementRef;
  @ViewChild("popupTemplate", { static: true }) popupTemplate!: TemplateRef<any>;
  public params: any;

  private static readonly DEFAULT_COLORS = ['#2ecc71', '#d6164a', '#1363df', '#f39c12', '#8e44ad'];

  allPoints: any[] = [];
  points: any[] = [];
  xField = 'period';
  yFields: string[] = [];
  labels: string[] = [];
  colors: string[] = [];
  columnWidth = 120;
  popupTitle = '';
  xAxisLabel = '';
  yAxisLabel = '';
  isPercent = false;
  popupCards: { label: string, value: string, arrow?: string, color?: string }[] = [];

  private static readonly TREND_GOOD = '#059669';
  private static readonly TREND_BAD = '#D11044';

  /** Horizontal cell padding; the grid sets the same value on this column's cellStyle. */
  static readonly CELL_PADDING = 4;

  private tooltipEl: HTMLDivElement | null = null;
  private watchedColumn: any = null;
  private overlayRef: OverlayRef | null = null;
  private popupChartEl: HTMLDivElement | null = null;
  private popupResizeSub: Subscription | null = null;
  /** Index range [start, end] selected by the popup's brush. */
  private brushRange: [number, number] | null = null;
  private readonly hideTooltip = () => { if (this.tooltipEl) this.tooltipEl.style.opacity = '0'; };
  // Redraw whenever the user (or auto-size) changes the column width
  private readonly onColumnWidthChanged = () => {
    this.columnWidth = this.params?.column?.getActualWidth?.() || this.columnWidth;
    this.createChart();
  };

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
  ) {}

  agInit(params: any): void {
    this.setup(params);
    this.createChart();
  }

  ngAfterViewInit(): void {
    window.addEventListener('scroll', this.hideTooltip, { capture: true });

    setTimeout(() => {
      this.params?.api?.resetRowHeights();
    }, 0);
  }

  refresh(params: any): boolean {
    this.setup(params);
    this.createChart();
    if (this.overlayRef) {
      this.buildPopupCards();
      this.cdr.detectChanges();
      this.renderPopupChart();
    }
    return true;
  }

  ngOnDestroy(): void {
    this.closePopup();
    this.watchedColumn?.removeEventListener?.('widthChanged', this.onColumnWidthChanged);
    this.watchedColumn = null;
    window.removeEventListener('scroll', this.hideTooltip, { capture: true });
    this.tooltipEl?.remove();
    this.tooltipEl = null;
  }

  private setup(params: any): void {
    this.params = params;
    const res = params?.res || {};

    this.xField = res?.lineChartX_Axis || 'period';
    this.yFields = this.splitList(res?.lineChartY_Axis);
    // Tooltip labels come only from lineChart_Tooltip_Name, matched to lineChartY_Axis by position
    const tooltipNames = this.splitList(res?.lineChart_Tooltip_Name);
    this.labels = this.yFields.map((_, i) => tooltipNames[i] || '');
    const customColors = this.splitList(res?.lineChartColors);
    this.colors = this.yFields.map((_, i) =>
      customColors[i] || BasicSparklineGridComponent.DEFAULT_COLORS[i % BasicSparklineGridComponent.DEFAULT_COLORS.length]
    );

    // Keep only the most recent N points (e.g. last 12 months) in the cell; the popup shows all
    this.allPoints = this.parsePoints(params?.value);
    const defaultView = parseInt(res?.lineChart_DefaultView, 10);
    this.points = defaultView > 0 && this.allPoints.length > defaultView
      ? this.allPoints.slice(-defaultView)
      : this.allPoints;

    this.popupTitle = params?.colDef?.headerName || params?.colDef?.field || '';
    this.xAxisLabel = String(res?.lineChart_XAxisLabel ?? '').trim();
    this.yAxisLabel = String(res?.lineChart_YAxisLabel ?? '').trim();
    this.isPercent = String(res?.lineChart_Format ?? '').trim().toLowerCase() === 'percent';
    this.columnWidth = params?.column?.getActualWidth?.() || 120;

    if (params?.column !== this.watchedColumn) {
      this.watchedColumn?.removeEventListener?.('widthChanged', this.onColumnWidthChanged);
      this.watchedColumn = params?.column || null;
      this.watchedColumn?.addEventListener?.('widthChanged', this.onColumnWidthChanged);
    }
  }

  private splitList(v: any): string[] {
    if (Array.isArray(v)) return v.map(s => String(s).trim()).filter(Boolean);
    if (typeof v !== 'string') return [];
    return v.split(',').map(s => s.trim()).filter(Boolean);
  }

  /** Accepts the aggregated JSON string or an already-parsed array. */
  private parsePoints(raw: any): any[] {
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== 'string' || !raw.trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private formatX(v: any): string {
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v ?? '') : d3.timeFormat('%b %Y')(d);
  }

  private formatValue(v: any): string {
    if (v === null || v === undefined || v === '') return '-';
    const n = Number(v);
    if (isNaN(n)) return String(v);
    // Values already arrive on a 0–100 scale for percent columns, so only the sign is added
    return String(Math.round(n * 100) / 100) + (this.isPercent ? '%' : '');
  }

  /** Chart spans the full AG Grid column width, less the small cell padding. */
  private resolveWidth(): number {
    return Math.max(this.columnWidth - BasicSparklineGridComponent.CELL_PADDING * 2, 40);
  }

  private createChart(): void {
    const element = this.gridSparklineContainer.nativeElement;
    d3.select(element).selectAll("*").remove();
    this.hideTooltip();

    if (!this.points.length || !this.yFields.length) return;

    const width = this.resolveWidth();
    const height = 44;
    const margin = { top: 6, right: 4, bottom: 10, left: 4 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3.select(element)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('display', 'block');

    // Light panel behind the lines
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('rx', 3)
      .attr('fill', '#f4f7fa00');

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const n = this.points.length;
    const xScale = d3.scaleLinear()
      .domain([0, Math.max(n - 1, 1)])
      .range([0, innerW]);

    const allValues: number[] = [];
    this.points.forEach(p => this.yFields.forEach(f => allValues.push(Number(p?.[f]) || 0)));
    // Anchor the baseline at 0 so zero values sit on the bottom edge
    const minVal = Math.min(0, d3.min(allValues) ?? 0);
    const maxVal = d3.max(allValues) ?? 0;
    const topPad = (maxVal - minVal) * 0.1 || 1;
    const yScale = d3.scaleLinear()
      .domain([minVal, maxVal + topPad])
      .range([innerH, 0]);

    // Tick marks along the bottom, one per data point
    g.selectAll('line.tick')
      .data(this.points)
      .enter()
      .append('line')
      .attr('class', 'tick')
      .attr('x1', (_: any, i: number) => xScale(i))
      .attr('x2', (_: any, i: number) => xScale(i))
      .attr('y1', innerH + 4)
      .attr('y2', innerH + 8)
      .attr('stroke', '#9aa5b1')
      .attr('stroke-width', 1);

    this.drawLines(g, this.points, xScale, yScale, 2);

    const hoverRect = this.addHoverLayer(svg, g, this.points, xScale, yScale, innerH, { x: 0, y: 0, width, height }, 3.5);
    // Single click anywhere on the sparkline opens the popup chart
    hoverRect
      .style('cursor', 'pointer')
      .on('click', (event: MouseEvent) => {
        event.stopPropagation();
        this.openPopup();
      });
  }

  private drawLines(g: any, points: any[], xScale: d3.ScaleLinear<number, number>, yScale: d3.ScaleLinear<number, number>, strokeWidth: number): void {
    this.yFields.forEach((field, idx) => {
      const line = d3.line<any>()
        .x((_: any, i: number) => xScale(i))
        .y((p: any) => yScale(Number(p?.[field]) || 0))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(points)
        .attr('fill', 'none')
        .attr('stroke', this.colors[idx])
        .attr('stroke-width', strokeWidth)
        .attr('stroke-linecap', 'round')
        .attr('d', line);
    });
  }

  /** Hover guide + point markers + tooltip, hidden until the pointer is over the chart. Returns the capture rect. */
  private addHoverLayer(target: any, g: any, points: any[], xScale: d3.ScaleLinear<number, number>, yScale: d3.ScaleLinear<number, number>,
                        innerH: number, area: { x: number, y: number, width: number, height: number }, markerRadius: number): any {
    const n = points.length;
    const guide = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#9aa5b1')
      .attr('stroke-dasharray', '2,2')
      .style('opacity', 0);

    const markers = this.yFields.map((_, idx) =>
      g.append('circle')
        .attr('r', markerRadius)
        .attr('fill', '#fff')
        .attr('stroke', this.colors[idx])
        .attr('stroke-width', 2)
        .style('opacity', 0)
    );

    const showAt = (event: MouseEvent) => {
      const [mx] = d3.pointer(event, g.node());
      const i = Math.max(0, Math.min(n - 1, Math.round(xScale.invert(mx))));
      const p = points[i];
      const x = xScale(i);

      guide.attr('x1', x).attr('x2', x).style('opacity', 1);
      markers.forEach((m, idx) =>
        m.attr('cx', x).attr('cy', yScale(Number(p?.[this.yFields[idx]]) || 0)).style('opacity', 1)
      );
      this.showTooltip(event, p);
    };

    return target.append('rect')
      .attr('x', area.x)
      .attr('y', area.y)
      .attr('width', area.width)
      .attr('height', area.height)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', showAt)
      .on('mouseenter', showAt)
      .on('mouseleave', () => {
        guide.style('opacity', 0);
        markers.forEach(m => m.style('opacity', 0));
        this.hideTooltip();
      });
  }

  /** Open the popup line chart using CDK Overlay (same pattern as the KPI chart fullscreen view). */
  openPopup(): void {
    if (this.overlayRef || !this.allPoints.length || !this.yFields.length) return;
    this.hideTooltip();
    this.brushRange = null;
    this.buildPopupCards();

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'grid-sparkline-popup-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: '100vw',
      height: '100vh',
    });

    this.overlayRef.backdropClick().subscribe(() => this.closePopup());
    this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') this.closePopup();
    });

    this.overlayRef.attach(new TemplatePortal(this.popupTemplate, this.viewContainerRef));
    this.cdr.detectChanges();

    // Wait for the overlay to lay out so the chart can size itself to the container
    setTimeout(() => {
      this.popupChartEl = this.overlayRef?.overlayElement?.querySelector('.sparkline-popup-chart') as HTMLDivElement;
      this.renderPopupChart();

      this.popupResizeSub = fromEvent(window, 'resize')
        .pipe(debounceTime(150))
        .subscribe(() => this.renderPopupChart());
    }, 50);
  }

  /** Category, patient count and one "% Change" card per series (latest vs previous point). */
  private buildPopupCards(): void {
    const cards: { label: string, value: string, arrow?: string, color?: string }[] = [];

    const category = this.resolveCategory();
    if (category) cards.push(category);

    const count = this.resolveCount();
    if (count) cards.push(count);

    const res = this.params?.res || {};
    const inverseFields = this.splitList(res?.lineChart_InverseTrend);
    const n = this.allPoints.length;

    this.yFields.forEach((field, idx) => {
      const label = this.labels[idx] || field;
      const card: { label: string, value: string, arrow?: string, color?: string } = { label: `% Change ${label}`, value: '-' };

      const latest = this.toNumber(this.allPoints[n - 1]?.[field]);
      const previous = this.toNumber(this.allPoints[n - 2]?.[field]);
      if (latest !== null && previous !== null) {
        const change = previous === 0 ? (latest === 0 ? 0 : null) : ((latest - previous) / Math.abs(previous)) * 100;
        if (change !== null) {
          // One decimal, but a change that rounds to zero shows as plain "0%" with no arrow
          const rounded = Math.round(Math.abs(change) * 10) / 10;
          card.value = rounded === 0 ? '0%' : `${rounded.toFixed(1)}%`;
          if (rounded !== 0) {
            // Abandon-type series: a rise is bad. Fill-type series: a rise is good.
            const inverse = inverseFields.length
              ? inverseFields.includes(field)
              : /abandon/i.test(field) || /abandon/i.test(label);
            const up = change > 0;
            card.arrow = up ? '▲' : '▼';
            card.color = up !== inverse ? BasicSparklineGridComponent.TREND_GOOD : BasicSparklineGridComponent.TREND_BAD;
          }
        }
      }
      cards.push(card);
    });

    this.popupCards = cards;
  }

  /**
   * Category card: lineChart_CategoryField, else the Show By column (headerReplacedbyshowby,
   * labelled with the selected Show By name), else the row group (key + group column header),
   * else the first column that is not a rank.
   */
  private resolveCategory(): { label: string, value: string } | null {
    const api = this.params?.api;
    const node = this.params?.node;
    const configured = this.params?.res?.lineChart_CategoryField;
    const displayed = this.params?.columnApi?.getAllDisplayedColumns?.() || [];

    if (configured) {
      const col = this.params?.columnApi?.getColumn?.(configured);
      const value = col ? api?.getValue?.(col, node) : (node?.data?.[configured] ?? node?.aggData?.[configured]);
      return { label: col?.getColDef?.()?.headerName || configured, value: this.displayText(value) };
    }

    // The header of a Show By column is already the selected Show By name (hierarchical-grid's showByHeaderName)
    const showByCol = displayed.find((c: any) => {
      const flag = c.getColDef?.()?.selectedRowConfig?.headerReplacedbyshowby;
      return flag === true || flag === 'true' || flag?.value === 'true';
    });
    const showByValue = showByCol ? api?.getValue?.(showByCol, node) : undefined;
    if (showByCol && showByValue !== null && showByValue !== undefined && showByValue !== '') {
      return { label: showByCol.getColDef()?.headerName || showByCol.getColId(), value: this.displayText(showByValue) };
    }

    if (node?.group) {
      const groupCol = node.rowGroupColumn;
      return { label: groupCol?.getColDef?.()?.headerName || 'Category', value: this.displayText(node.key) };
    }

    const isRank = (c: any) => /rank/i.test(c.getColDef?.()?.headerName || '') || /rank/i.test(c.getColId?.() || '');
    const first = displayed
      .find((c: any) => c !== this.params?.column && !c.getColDef?.()?.cellRenderer && !isRank(c));
    if (!first) return null;
    return { label: first.getColDef()?.headerName || first.getColId(), value: this.displayText(api?.getValue?.(first, node)) };
  }

  /** Patient count card: lineChart_CountField, else the first column whose header mentions "patient". */
  private resolveCount(): { label: string, value: string } | null {
    const api = this.params?.api;
    const node = this.params?.node;
    const columnApi = this.params?.columnApi;
    const configured = this.params?.res?.lineChart_CountField;

    const col = configured
      ? columnApi?.getColumn?.(configured)
      : (columnApi?.getAllColumns?.() || []).find((c: any) =>
          c !== this.params?.column && /patient/i.test(c.getColDef?.()?.headerName || c.getColId?.() || ''));

    if (!col && !configured) return null;
    const raw = col ? api?.getValue?.(col, node) : (node?.data?.[configured] ?? node?.aggData?.[configured]);
    // Grouped rows can hold an aggregation object ({ value })
    const value = raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw;
    const num = this.toNumber(value);
    return {
      label: col?.getColDef?.()?.headerName || configured || 'Patient Count',
      value: num !== null ? num.toLocaleString() : this.displayText(value),
    };
  }

  private toNumber(v: any): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  }

  private displayText(v: any): string {
    if (v === null || v === undefined || v === '') return '-';
    return String(v);
  }

  closePopup(): void {
    this.popupResizeSub?.unsubscribe();
    this.popupResizeSub = null;
    this.popupChartEl = null;
    this.hideTooltip();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  /**
   * Popup chart: a focus line chart (axes + gridlines) over a brush strip that
   * shows the full series. Dragging the brush zooms the focus chart to that range.
   */
  private renderPopupChart(): void {
    const element = this.popupChartEl;
    if (!element) return;
    d3.select(element).selectAll("*").remove();
    this.hideTooltip();

    const allPoints = this.allPoints;
    if (!allPoints.length || !this.yFields.length) return;

    const n = allPoints.length;
    const width = Math.max(element.clientWidth, 300);
    const height = Math.max(element.clientHeight, 200);
    // Extra room on the left / under the x-axis when axis titles are configured
    const margin = { top: 24, right: 28, bottom: 16, left: this.yAxisLabel ? 76 : 56 };
    const contextH = 52;
    const tickLabelH = 62; // room for angled period labels under the focus chart
    const gapH = tickLabelH + (this.xAxisLabel ? 22 : 0); // (+ x-axis title)
    const innerW = width - margin.left - margin.right;
    const focusH = height - margin.top - margin.bottom - contextH - gapH;

    // Keep the brushed index range across re-renders (resize / refresh); default to lineChart_DefaultView
    if (!this.brushRange) {
      const defaultView = parseInt(this.params?.res?.lineChart_DefaultView, 10);
      this.brushRange = defaultView > 0 && n > defaultView ? [n - defaultView, n - 1] : [0, n - 1];
    }
    this.brushRange = [Math.max(0, Math.min(this.brushRange[0], n - 1)), Math.max(0, Math.min(this.brushRange[1], n - 1))];

    const svg = d3.select(element)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('display', 'block');

    const focusG = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    const contextG = svg.append('g').attr('transform', `translate(${margin.left},${margin.top + focusH + gapH})`);

    // Shared y domain so zooming doesn't make the lines jump
    const allValues: number[] = [];
    allPoints.forEach(p => this.yFields.forEach(f => allValues.push(Number(p?.[f]) || 0)));
    const minVal = Math.min(0, d3.min(allValues) ?? 0);
    const maxVal = d3.max(allValues) ?? 0;
    const yDomain: [number, number] = [minVal, maxVal > minVal ? maxVal : minVal + 1];

    const fontFamily = "'Poppins'";

    // Axis titles (lineChart_YAxisLabel / lineChart_XAxisLabel)
    if (this.yAxisLabel) {
      svg.append('text')
        .attr('transform', `translate(16,${margin.top + focusH / 2}) rotate(-90)`)
        .attr('text-anchor', 'middle')
        .attr('font-family', fontFamily)
        .attr('font-size', 12)
        .attr('fill', '#737D88')
        .text(this.yAxisLabel);
    }
    if (this.xAxisLabel) {
      svg.append('text')
        .attr('x', margin.left + innerW / 2)
        .attr('y', margin.top + focusH + tickLabelH + 10)
        .attr('text-anchor', 'middle')
        .attr('font-family', fontFamily)
        .attr('font-size', 12)
        .attr('fill', '#737D88')
        .text(this.xAxisLabel);
    }

    const drawFocus = () => {
      focusG.selectAll('*').remove();
      this.hideTooltip();

      const [i0, i1] = this.brushRange!;
      const points = allPoints.slice(i0, i1 + 1);
      const m = points.length;

      const xScale = d3.scaleLinear().domain([0, Math.max(m - 1, 1)]).range([0, innerW]);
      const yScale = d3.scaleLinear().domain(yDomain).nice().range([focusH, 0]);

      // Y axis with horizontal gridlines
      const yAxis = focusG.append('g')
        .call(d3.axisLeft(yScale).ticks(5).tickSize(-innerW).tickFormat((v: d3.NumberValue) => this.formatValue(+v)));
      yAxis.select('.domain').remove();
      yAxis.selectAll('.tick line').attr('stroke', '#e5e7eb');
      yAxis.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', 11).attr('font-family', fontFamily).attr('dx', -4);

      // X axis: a label for every period; angle them instead of dropping any when they don't fit
      // (~70px for a flat "Mon YYYY" label, ~16px per label at 45°, tighter than that goes vertical)
      const spacing = m > 1 ? innerW / (m - 1) : innerW;
      const angle = spacing >= 70 ? 0 : spacing >= 16 ? -45 : -90;
      const xAxis = focusG.append('g')
        .attr('transform', `translate(0,${focusH})`)
        .call(d3.axisBottom(xScale)
          .tickValues(d3.range(0, m))
          .tickFormat((i: d3.NumberValue) => this.formatX(points[+i]?.[this.xField]))
          .tickSizeOuter(0));
      xAxis.select('.domain').attr('stroke', '#d1d5db');
      xAxis.selectAll('.tick line').attr('stroke', '#d1d5db');
      const tickText = xAxis.selectAll('.tick text')
        .attr('fill', '#6b7280')
        .attr('font-size', angle === -90 ? Math.max(8, Math.min(11, spacing - 2)) : 11)
        .attr('font-family', fontFamily);
      if (angle === 0) {
        tickText.attr('dy', 12);
      } else if (angle === -45) {
        // Rotate around the tick so the end of each label sits under its point
        tickText
          .attr('text-anchor', 'end')
          .attr('dx', '-0.8em')
          .attr('dy', '0.15em')
          .attr('transform', 'rotate(-45)');
      } else {
        tickText
          .attr('text-anchor', 'end')
          .attr('x', -9)
          .attr('y', 0)
          .attr('dy', '0.35em')
          .attr('transform', 'rotate(-90)');
      }

      this.drawLines(focusG, points, xScale, yScale, 3);

      // Small dots at each data point for visual anchoring
      this.yFields.forEach((field, idx) => {
        focusG.selectAll(null)
          .data(points)
          .enter()
          .append('circle')
          .attr('cx', (_: any, i: number) => xScale(i))
          .attr('cy', (p: any) => yScale(Number(p?.[field]) || 0))
          .attr('r', m > 60 ? 0 : 3)
          .attr('fill', this.colors[idx])
          .attr('stroke', '#fff')
          .attr('stroke-width', 1.5)
          .style('pointer-events', 'none');
      });

      this.addHoverLayer(focusG, focusG, points, xScale, yScale, focusH, { x: 0, y: 0, width: innerW, height: focusH }, 5);
    };

    // Context strip: full series + brush
    const ctxX = d3.scaleLinear().domain([0, Math.max(n - 1, 1)]).range([0, innerW]);
    const ctxY = d3.scaleLinear().domain(yDomain).range([contextH, 0]);

    this.drawLines(contextG, allPoints, ctxX, ctxY, 1.5);
    this.yFields.forEach((field, idx) => {
      contextG.selectAll(null)
        .data(allPoints)
        .enter()
        .append('circle')
        .attr('cx', (_: any, i: number) => ctxX(i))
        .attr('cy', (p: any) => ctxY(Number(p?.[field]) || 0))
        .attr('r', 2)
        .attr('fill', this.colors[idx])
        .style('pointer-events', 'none');
    });

    // Brush styling matches the dynamic bar chart: grey selection, white rounded
    // handles with a drop shadow and two grip lines on each handle
    const handleSize = 10;
    const shadowId = 'sparklinePopupBrushShadow';
    const filter = svg.append('defs').append('filter').attr('id', shadowId).attr('width', 10).attr('height', 24);
    filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 1).attr('result', 'blur');
    filter.append('feOffset').attr('in', 'blur').attr('dx', 1).attr('dy', 1).attr('result', 'offsetBlur');
    filter.append('feFlood').attr('in', 'offsetBlur').attr('flood-color', '#000000').attr('flood-opacity', 0.4).attr('result', 'offsetColor');
    filter.append('feComposite').attr('in', 'offsetColor').attr('in2', 'offsetBlur').attr('operator', 'in').attr('result', 'offsetBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // d3 resets the handle geometry on every move, so re-apply the style each time
    const styleHandles = (sel: [number, number] | null) => {
      brushG.selectAll('.handle')
        .attr('fill', 'white')
        .attr('rx', 4).attr('ry', 4)
        .attr('y', (contextH - 24) / 2)
        .attr('height', 24)
        .style('filter', `url(#${shadowId})`);
      gripLines.attr('visibility', sel ? 'visible' : 'hidden');
      if (!sel) return;
      gripLines.each(function (this: SVGLineElement, d: number) {
        const handleX = sel[d < 2 ? 0 : 1] - handleSize / 2;
        const x = handleX + (d % 2 === 0 ? 3 : 7);
        d3.select(this).attr('x1', x).attr('x2', x);
      });
    };

    const brush = d3.brushX()
      .handleSize(handleSize)
      .extent([[0, 0], [innerW, contextH]])
      .on('start brush end', (event: d3.D3BrushEvent<unknown>) => {
        const sel = event.selection as [number, number] | null;
        styleHandles(sel);
        if (!sel) {
          // A click without dragging clears the brush: reset to the full range
          if (event.type === 'end' && event.sourceEvent) brushG.call(brush.move, [0, innerW]);
          return;
        }
        let i0 = Math.max(0, Math.round(ctxX.invert(sel[0])));
        let i1 = Math.min(n - 1, Math.round(ctxX.invert(sel[1])));
        if (i1 <= i0) { i1 = Math.min(n - 1, i0 + 1); i0 = Math.max(0, i1 - 1); }

        if (i0 !== this.brushRange![0] || i1 !== this.brushRange![1]) {
          this.brushRange = [i0, i1];
          drawFocus();
        }
        // Snap the brush edges to the nearest data points once the user lets go
        if (event.type === 'end' && event.sourceEvent) {
          brushG.call(brush.move, [ctxX(i0), ctxX(i1)]);
        }
      });

    const brushG = contextG.append('g').attr('class', 'popup-brush').call(brush);
    brushG.select('.overlay').style('fill', 'transparent');
    brushG.select('.selection')
      .attr('fill', '#A0A0A0')
      .attr('fill-opacity', '0.2')
      .style('stroke', '#E8EAEE')
      .style('stroke-width', '1');

    // Two grip lines per handle: data 0,1 → west handle, 2,3 → east handle
    const gripLines = contextG.selectAll('line.brush-grip')
      .data([0, 1, 2, 3])
      .enter()
      .append('line')
      .attr('class', 'brush-grip')
      .attr('y1', (contextH - 12) / 2)
      .attr('y2', (contextH - 12) / 2 + 12)
      .style('stroke', '#8A98AB')
      .style('stroke-width', '1')
      .style('pointer-events', 'none');

    drawFocus();
    brushG.call(brush.move, [ctxX(this.brushRange[0]), ctxX(this.brushRange[1])]);
  }

  /**
   * Dark floating tooltip, mounted outside the grid cell so its overflow can't clip it.
   *
   * CDK 20+ renders overlays (this popup, MatDialog, etc.) as popovers in the browser
   * top layer, which paints above every z-index in the page — and browser fullscreen
   * only paints the fullscreen element. So the tooltip is mounted inside the nearest
   * top-layer ancestor of the hovered chart, falling back to <body>.
   */
  private showTooltip(event: MouseEvent, point: any): void {
    if (!this.tooltipEl) {
      this.tooltipEl = document.createElement('div');
      Object.assign(this.tooltipEl.style, {
        position: 'fixed',
        // Max z-index: must sit above the sparkline popup and any host popup/sheet (some use 999999+)
        zIndex: '2147483647',
        pointerEvents: 'none',
        background: 'rgba(17, 17, 17, 0.92)',
        color: '#fff',
        borderRadius: '6px',
        padding: '8px 10px',
        fontSize: '12px',
        lineHeight: '1.4',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
        whiteSpace: 'nowrap',
        opacity: '0',
        transition: 'opacity 0.1s ease',
      } as Partial<CSSStyleDeclaration>);
    }

    // Re-mount when the hovered chart's top-layer host changes (grid → popup, fullscreen
    // on/off). Appending also makes it the last child, so it wins z-index ties there.
    const target = event.target as Element | null;
    const host = target?.closest?.('.cdk-overlay-popover')
      || document.fullscreenElement
      || document.body;
    if (this.tooltipEl.parentElement !== host || host.lastElementChild !== this.tooltipEl) {
      host.appendChild(this.tooltipEl);
    }

    const rows = this.yFields.map((f, idx) => `
      <div style="display:flex;align-items:center;gap:8px;margin-top:4px;">
        <span style="width:8px;height:8px;border-radius:50%;background:${this.colors[idx]};flex-shrink:0;"></span>
        <span style="color:#d0d4da;">${this.escape(this.labels[idx])}</span>
        <span style="margin-left:auto;padding-left:12px;font-weight:600;">${this.escape(this.formatValue(point?.[f]))}</span>
      </div>`).join('');

    this.tooltipEl.innerHTML = `
      <div style="font-weight:600;padding-bottom:4px;border-bottom:1px solid rgba(255,255,255,0.15);">
        ${this.escape(this.formatX(point?.[this.xField]))}
      </div>${rows}`;

    // Position above-right of the pointer, flipping to stay inside the viewport
    const tip = this.tooltipEl;
    tip.style.opacity = '1';
    const { width: tw, height: th } = tip.getBoundingClientRect();
    let left = event.clientX + 12;
    let top = event.clientY - th - 10;
    if (left + tw > window.innerWidth - 8) left = event.clientX - tw - 12;
    if (top < 8) top = event.clientY + 14;
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
  }

  private escape(s: string): string {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }
}
